import type { components } from '@nutians/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';

type CustomFood = components['schemas']['CustomFood'];

export const CUSTOM_FOODS_KEY = ['custom-foods'] as const;

export function useCustomFoods() {
  return useQuery({
    queryKey: CUSTOM_FOODS_KEY,
    queryFn: async () => {
      const { data, error } = await api.GET('/me/custom-foods');
      if (error || !data) throw new Error('Failed to load custom foods');
      return data;
    },
    staleTime: 60_000,
  });
}

export function useCreateCustomFood() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      name: string;
      brand?: string;
      basis: 'per_100g' | 'per_serving';
      servingG?: number;
      macros: { kcal: number; proteinG: number; carbsG: number; fatG: number; fiberG: number };
    }) => {
      const { data, error } = await api.POST('/me/custom-foods', { body });
      if (error || !data) throw new Error('Failed to create custom food');
      return data as CustomFood;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: CUSTOM_FOODS_KEY }),
  });
}

export function useUpdateCustomFood() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: string;
      name?: string;
      brand?: string | null;
      macros?: { kcal: number; proteinG: number; carbsG: number; fatG: number; fiberG: number };
    }) => {
      const { id, ...body } = args;
      const { data, error } = await api.PATCH('/me/custom-foods/{id}', {
        params: { path: { id } },
        body,
      });
      if (error || !data) throw new Error('Failed to update custom food');
      return data as CustomFood;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: CUSTOM_FOODS_KEY }),
  });
}

export function useDeleteCustomFood() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.DELETE('/me/custom-foods/{id}', {
        params: { path: { id } },
      });
      if (error) throw new Error('Failed to delete custom food');
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: CUSTOM_FOODS_KEY }),
  });
}
